import { useEffect, useState } from "react";
import Clipboard from "clipboard";
import cn from "classnames";
import { FCX } from "@datassential/platform-ui-lib";
import CopyNotification from "@components/new/Table/CopyNotification";
import useNotification from "@core/hooks/useNotification";
import { IconCopyTable } from "@icons";
import { SalesStrategySection } from "@apiHooks/useSalesStrategy";
import Button from "@components/new/Button/Button";
import { buildStrategyCopyHtml, createCopyContainer, removeCopyContainer } from "./StrategyCopyButton.helpers";
import { COPY_TRIGGER_CLASS } from "./StrategyCopyButton.constants";
import { SSB_COPY_ARIA, SSB_COPY_ERROR, SSB_COPY_SUCCESS } from "../Strategy.constants";
import "./StrategyCopyButton.styles.scss";

interface StrategyCopyButtonProps {
    sections: SalesStrategySection[];
    userInput: string | null;
}

const StrategyCopyButton: FCX<StrategyCopyButtonProps> = ({ sections, userInput, className }) => {
    const { isOpen, handleShowNotification } = useNotification();
    const [notificationText, setNotificationText] = useState(SSB_COPY_SUCCESS);

    useEffect(() => {
        const clip = new Clipboard(`.${COPY_TRIGGER_CLASS}`, {
            target: () => createCopyContainer(buildStrategyCopyHtml(sections, userInput)),
        });

        clip.on("success", e => {
            e.clearSelection();
            removeCopyContainer();
            setNotificationText(SSB_COPY_SUCCESS);
            handleShowNotification();
        });

        clip.on("error", () => {
            removeCopyContainer();
            setNotificationText(SSB_COPY_ERROR);
            handleShowNotification();
        });

        return () => {
            clip.destroy();
            removeCopyContainer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- handleShowNotification's identity changes every render, but its behavior doesn't depend on sections/userInput, so omitting it doesn't cause staleness and it shouldn't rebind the Clipboard instance
    }, [sections, userInput]);

    return (
        <div className="StrategyCopyButton__wrapper">
            <Button
                isWrapper
                className={cn(COPY_TRIGGER_CLASS, className)}
                ariaLabel={SSB_COPY_ARIA}
                testId="ssb-copy-button"
            >
                <IconCopyTable />
            </Button>
            <CopyNotification isOpen={isOpen}>
                <span>{notificationText}</span>
            </CopyNotification>
        </div>
    );
};

export default StrategyCopyButton;
